/**
 * 네이버 검색광고 연동 — **고객이 SA API 키를 직접 입력**(방식 B, 설계 §7.8) → 검증 즉시 연동 → 사이트 선택.
 *
 *      not_submitted ──(customer ID · 엑세스라이선스 · 비밀키)──▶ select_channels → connected
 *
 * - 키는 네이버 광고센터 › 검색 광고 › 도구 › SA API 사용 관리 에 있다. 서버가 그 키로 네이버를 실제로 불러
 *   검증하고, 성공하면 그 자리에서 고정·연동한다(담당자·수락 대기 없음).
 * - 고객 키가 무효가 되면(재발급·삭제) 수집기가 `invalid_reason='naver_customer_key_invalid'` 를 찍는다 →
 *   같은 폼(번호 잠금)으로 다시 넣으면 풀린다.
 * - ⚠️ 권한 안내는 사실대로 — 이 키는 광고 수정 권한까지 있다. "저희는 성과 조회에만 써요"(O) /
 *   "조회만 가능한 키"(X, 거짓).
 *
 * ── 아래는 방식 A(번호 입력 → 담당자 요청 → 수락). **화면 진입점은 숨겼고**(아론 10-02) API 는 남아 있다 —
 *    이미 A 로 연동된 샵(치순당)은 그대로 '연동됨', A 접수가 진행 중인 샵은 대기 카드가 그대로 보인다.
 *    A 로 연동된 샵은 같은 번호로 키를 넣으면 자격증명만 B 로 바뀐다(§7.8.6).
 *
 * ⚠️ 다른 채널과 흐름이 다르다. 네이버 SA 에는 OAuth 가 없고 **연동 요청은 관리계정(우리)만** 보낼 수
 *    있으며 API 도 없다(네이버 FAQ 12·1782, 설계 §5.6.1). 그래서 고객은 번호만 주고, 담당자가 요청을 보내고,
 *    고객이 네이버에서 수락하면 이 화면이 감지한다. 단계는 서버가 `stage` 로 준다(설계 §7.7.3).
 *
 *      not_submitted → awaiting_request → awaiting_accept → select_channels → connected
 *        (번호 입력)     (저희 차례)          (고객 차례)          (사이트 선택)
 *
 * ⚠️ 권한 안내는 사실대로 한다 — 수락하면 관리계정 회원이 **운영 권한**(실측 `MASTER`)을 갖고,
 *    관리계정 회원 접근에는 네이버가 운영 알림을 보내지 않는다. "저희는 성과 조회만 해요"(O) /
 *    "조회만 가능해요"(X, 거짓).
 *
 * ⚠️ 사이트는 **빈 선택도 확정**이다("이 샵 몫 없음" — 수집은 돌고 전 행 제외). 미확정(null)과 다르다.
 *
 * 틀·용어: agent_docs/connect_ux.md (공통 틀 `SetupPage`). 모바일: 설정 페이지라 대응 O — 표는 `overflow-x-auto`.
 */
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import { getGetShopQueryKey } from '@/api/generated/v2-shop/v2-shop'
import {
  getGetNaverStatusQueryKey,
  getListNaverBusinessChannelsQueryKey,
  useCancelNaverLinkRequest,
  useConnectNaverApiKey,
  useDisconnectNaver,
  useGetNaverStatus,
  useListNaverBusinessChannels,
  useSaveNaverSettings,
} from '@/api/generated/v2-naver/v2-naver'
import type { NaverStatusResponse } from '@/api/schemas'
import { GuideStep } from '@/components/guide/GuideStep'
import { HiddenFeatureNotFound } from '@/components/HiddenFeatureNotFound'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { SetupPage } from '@/features/setup/SetupPage'
import { groupNaverSites, initialSiteKeys, selectedChannelIds } from '@/features/setup/naverSites'
import { useConfirm } from '@/hooks/useConfirm'
import { apiMessage } from '@/lib/apiError'
import { auth } from '@/lib/auth'
import { useShop } from '@/lib/shopContext'

export const Route = createFileRoute('/_dashboard/setup/naver')({
  component: NaverSetupPage,
})

const NAVER_ADS_URL = 'https://ads.naver.com/'

/** 고객 키가 무효(재발급·삭제) — 수집기가 찍는 사유(`v2/ad_insights/channels/naver.py`) */
const CUSTOMER_KEY_INVALID = 'naver_customer_key_invalid'

/** 대기 단계는 경고가 아니라 진행 중이다 — 회색 배지(connect_ux.md §3 진행 배지) */
const WAITING_LABEL: Record<string, string> = {
  not_submitted: '미연동',
  awaiting_request: '요청 대기',
  awaiting_accept: '수락 대기',
}

/**
 * ⭐ 히든 게이트 — 실험실 플래그(`shops.lab_features_enabled`, 어드민 토글)가 꺼진 샵이면 **첫 줄에서** 잘라낸다.
 *    본문을 분리해 둬서 허용 밖이면 쿼리도 나가지 않는다(틱톡·구글 화면과 같은 구조).
 * ⚠️ 이 플래그는 노출용이다 — 실제 차단은 `/v2/naver/*` 404(`v2/naver/gate.py`)가 한다.
 */
function NaverSetupPage() {
  const { shop } = useShop()
  if (!shop?.naver_enabled) return <HiddenFeatureNotFound />
  return <NaverSetupContent />
}

/** react-query 에러에서 HTTP 상태를 꺼낸다 — custom-instance 는 두 모양 중 하나로 던진다. */
function errorStatus(err: unknown): number | undefined {
  const e = err as { status?: number; response?: { status?: number } } | null
  return e?.status ?? e?.response?.status
}

/** 사유가 확정된 거절(422 고정 전·하위 아님, 503 서버 설정 누락)은 재시도해도 같다. */
function noRetryOnFinal(count: number, err: unknown): boolean {
  const s = errorStatus(err)
  return s !== 422 && s !== 503 && count < 2
}

function NaverSetupContent() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialog } = useConfirm()

  useEffect(() => {
    if (!auth.isAuthenticated()) navigate({ to: '/' })
  }, [navigate])

  const {
    data: status,
    isLoading,
    error: statusError,
    refetch: refetchStatus,
    isFetching: statusFetching,
  } = useGetNaverStatus({
    query: {
      enabled: auth.isAuthenticated(),
      // 404(허용 목록 밖)는 재시도하지 않는다 — 일시적 5xx·네트워크 오류는 그대로 재시도한다
      retry: (count, err) => errorStatus(err) !== 404 && count < 3,
    },
  })
  const statusNotFound = errorStatus(statusError) === 404
  // ⚠️ 상태를 **모르는** 것과 미연동은 다르다(틱톡 화면과 같은 이유) — 모르면 에러와 재시도만 그린다.
  const statusUnknown = !!statusError && !status && !statusNotFound

  // 상태·사이트 목록·샵 응답(사이드바 요약)은 함께 낡는다
  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: getGetNaverStatusQueryKey() })
    // 사이트 키는 계정별 문자열 하나(`/v2/naver/ad-accounts/{id}/business-channels`)라 접두어 매칭이 안 된다
    queryClient.invalidateQueries({
      predicate: (q) =>
        typeof q.queryKey[0] === 'string' && q.queryKey[0].endsWith('/business-channels'),
    })
    queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
  }

  const stage = status?.stage ?? 'not_submitted'
  const connected = !!status?.connected
  const invalid = connected && !!status?.invalid
  const ownKey = status?.credential_source === 'own_api_key'
  const keyInvalid = invalid && status?.invalid_reason === CUSTOMER_KEY_INVALID

  const disconnectMutation = useDisconnectNaver({
    mutation: {
      onSuccess: () => {
        refreshAll()
        // ⚠️ 우리 쪽만 끊었다 — 네이버 쪽(관리계정 연동·API 키)은 남아 있다는 걸 같이 말한다
        toast.success(
          ownKey
            ? '연동을 해제하고 저장된 API 키를 지웠어요. 키를 폐기하려면 SA API 사용 관리에서 비밀키를 재발급해 주세요'
            : '연동을 해제했어요. 네이버 쪽 관리계정 연동까지 끊으려면 네이버 광고센터에서 [연동 해제]를 눌러 주세요',
          { duration: 8000 },
        )
      },
      // "이미 해제된" 거절은 화면이 낡았다는 뜻이다 — 다시 읽는다
      onError: (e: unknown) => {
        toast.error(apiMessage(e, '연동을 해제하지 못했어요'))
        refreshAll()
      },
    },
  })


  const guide = (
    <div className="space-y-4">
      <p className="text-sm text-on-surface-variant">
        네이버 검색광고 계정을 연동하면 캠페인·광고별 광고 성과(노출·클릭·비용·전환)를 매일 자동으로
        불러와요. 광고를 만들거나 수정하지 않아요.
      </p>
      <GuideStep number={1} title="SA API 사용 관리 열기">
        <p>
          네이버 광고센터에 이 쇼핑몰의 광고 계정으로 로그인한 뒤 검색 광고 › 도구 › SA API 사용 관리로 들어가
          주세요. API 라이선스가 아직 없으면 그 화면에서 먼저 발급해 주세요.
        </p>
      </GuideStep>
      <GuideStep number={2} title="세 값 복사해서 입력">
        <p>
          화면 위쪽 &lsquo;현재 접근 중인 광고계정&rsquo;의 customer ID(숫자), 표의 엑세스라이선스와 비밀키를
          아래 칸에 그대로 붙여 넣고 &lsquo;확인하고 연동&rsquo;을 눌러 주세요. 저희가 그 자리에서 키를 확인하고
          바로 연동해요.
        </p>
      </GuideStep>
      <GuideStep number={3} title="이 쇼핑몰의 사이트 선택">
        <p>
          광고 계정에 등록된 사이트 중 이 쇼핑몰 것을 골라 &lsquo;저장&rsquo;을 누르면 다음 날부터 매일 광고 성과를
          불러와요.
        </p>
      </GuideStep>
      {/* ⚠️ 권한 사실 — "조회만 가능한 키" 라고 쓰지 않는다(SA API 키는 수정 권한까지 있다) */}
      <p className="text-xs text-on-surface-variant">
        이 키는 광고를 수정할 수 있는 권한까지 있는 키예요. 저희는 광고 성과 조회에만 써요. 연동을 끊고 싶으시면
        언제든 SA API 사용 관리 화면에서 비밀키를 재발급해 주세요. 재발급하면 기존 키는 바로 쓸 수 없게 돼요.
      </p>
    </div>
  )

  if (statusNotFound) return <HiddenFeatureNotFound />

  if (statusUnknown) {
    return (
      <div className="max-w-xl py-16 px-4">
        <div className="flex gap-2 p-4 rounded-lg bg-red-50 text-red-900 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p>{apiMessage(statusError, '네이버 연동 상태를 불러오지 못했어요. 잠시 후 다시 시도해 주세요')}</p>
            <button
              type="button"
              onClick={() => refetchStatus()}
              disabled={statusFetching}
              className="mt-2 min-h-[44px] sm:min-h-0 text-sm font-medium underline"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading || !status) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-on-surface-variant" />
      </div>
    )
  }

  return (
    <SetupPage
      title="네이버 검색광고 연동"
      connected={connected}
      disconnectedLabel={WAITING_LABEL[stage] ?? '미연동'}
      warningBadge={
        keyInvalid
          ? '재연동 필요'
          : invalid
            ? '권한 확인 필요'
            : stage === 'select_channels'
              ? '사이트 선택 필요'
              : undefined
      }
      externalLink={NAVER_ADS_URL}
      externalLinkLabel="네이버 광고센터 열기"
      guide={guide}
      disconnect={
        connected
          ? {
              description: '연동을 해제하면 이 쇼핑몰의 네이버 광고 성과 수집이 멈춰요. 이미 불러온 성과는 남아요.',
              loading: disconnectMutation.isPending,
              onClick: async () => {
                const ok = await confirm(
                  ownKey
                    ? '이 쇼핑몰의 네이버 광고 성과 수집이 멈추고, 저희가 보관한 API 키를 지워요. 이미 불러온 성과는 남아요. 키 자체를 폐기하려면 SA API 사용 관리 화면에서 비밀키를 재발급해 주세요.'
                    : '이 쇼핑몰의 네이버 광고 성과 수집이 멈춰요. 이미 불러온 성과는 남아요. 네이버 쪽 관리계정 연동까지 끊으려면 네이버 광고센터에서 [연동 해제]를 눌러 주세요.',
                  { title: '네이버 연동을 해제할까요?', confirmText: '연동 해제', variant: 'danger' },
                )
                if (ok) disconnectMutation.mutate()
              },
            }
          : undefined
      }
    >
      <div className="space-y-5">
        {/* 재조회가 실패했지만 직전 상태는 있다 — 보이는 상태가 낡았을 수 있음을 알린다 */}
        {statusError && (
          <div className="flex gap-2 p-4 rounded-lg bg-red-50 text-red-900 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>{apiMessage(statusError, '연동 상태를 불러오지 못했어요. 잠시 후 다시 시도해 주세요')}</p>
          </div>
        )}

        {invalid && (
          <div className="flex gap-2 p-4 rounded-lg bg-amber-50 text-amber-900 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            {keyInvalid ? (
              // 방식 B — 고객이 키를 재발급·삭제했다. 같은 계정의 새 키를 넣으면 바로 풀린다
              <p>
                저장된 API 키로 네이버에 접속할 수 없어요. 키를 재발급하셨다면 아래에 새 엑세스라이선스와 비밀키를
                넣어 주세요.
              </p>
            ) : status.invalid_reason === 'customer_unpinned' ? (
              // 담당자 확정(고정)이 풀린 채 연동만 남았다 — 수집기가 거부한다. 해제 후 다시 입력하면 빠져나온다
              <p>
                이 쇼핑몰에 지정된 광고 계정 정보가 바뀌어 광고 성과를 불러올 수 없어요. 아래 &lsquo;연동 해제&rsquo;를 누른 뒤
                광고 계정 번호를 다시 입력해 주세요.
              </p>
            ) : (
              <p>
                광고 성과를 불러오지 못했어요. 네이버에서 관리계정 연동이 해제됐을 수 있어요 — 네이버 광고센터에서
                연동 상태를 확인해 주세요.
              </p>
            )}
          </div>
        )}

        {stage === 'not_submitted' && <ApiKeyForm onStale={refreshAll} />}
        {keyInvalid && status.customer_id && <ApiKeyForm lockedCustomerId={status.customer_id} onStale={refreshAll} />}
        {(stage === 'awaiting_request' || stage === 'awaiting_accept') && (
          <WaitingCard
            status={status}
            onRecheck={() => refetchStatus()}
            rechecking={statusFetching}
            onStale={refreshAll}
          />
        )}
        {connected && status.customer_id && (
          <>
            <div className="space-y-1">
              <p className="text-sm text-on-surface-variant">
                연동된 광고 계정:{' '}
                <span className="font-medium text-on-surface">
                  {status.account_name ? `${status.account_name} (${status.customer_id})` : status.customer_id}
                </span>
              </p>
              {ownKey && status.api_key_hint && (
                <p className="text-sm text-on-surface-variant">
                  연동 방식: API 키 (엑세스라이선스 {status.api_key_hint})
                </p>
              )}
            </div>
            <SiteSection status={status} customerId={status.customer_id} onStale={refreshAll} />
            {/* 같은 계정의 키를 넣으면 자격증명만 바뀐다(사이트 선택·소급 커서 그대로, §7.8.6).
                A 샵 = 방식 전환, B 샵 = 키 재발급을 미리 반영(무효가 찍혀 하루치가 빠지기 전에) */}
            {!invalid && (
              <details className="pt-4 border-t border-outline-variant">
                <summary className="cursor-pointer text-sm font-medium text-on-surface min-h-[44px] sm:min-h-0 flex items-center">
                  {ownKey ? 'API 키 바꾸기' : 'API 키로 연동 방식 바꾸기'}
                </summary>
                <div className="pt-3">
                  <ApiKeyForm lockedCustomerId={status.customer_id} onStale={refreshAll} />
                </div>
              </details>
            )}
          </>
        )}
      </div>
      <ConfirmDialog />
    </SetupPage>
  )
}

/**
 * ① 미연동 · 재연동 필요 — customer ID · 엑세스라이선스 · 비밀키 입력(방식 B, 설계 §7.8).
 *
 * 서버가 그 키로 네이버를 실제로 불러 검증하고, 성공하면 바로 연동한다. `lockedCustomerId` 가 있으면
 * 번호를 고정한다(재연동 필요 회복·A→B 전환 — 다른 계정으로 바꾸려면 해제부터).
 *
 * ⚠️ 비밀키는 화면 상태에만 잠깐 둔다 — 성공·실패와 관계없이 보낸 뒤 칸을 비운다. 자동완성도 끈다.
 */
function ApiKeyForm({ lockedCustomerId, onStale }: { lockedCustomerId?: string; onStale: () => void }) {
  const queryClient = useQueryClient()
  const [customerId, setCustomerId] = useState(lockedCustomerId ?? '')
  const [apiKey, setApiKey] = useState('')
  const [apiSecret, setApiSecret] = useState('')
  const cid = customerId.trim()
  const invalidCid = cid !== '' && !/^\d+$/.test(cid)
  const ready = !!cid && !invalidCid && !!apiKey.trim() && !!apiSecret.trim()

  const connect = useConnectNaverApiKey({
    mutation: {
      onSuccess: (next) => {
        queryClient.setQueryData(getGetNaverStatusQueryKey(), next)
        // 사이드바 요약(`shop.naver`)을 따라오게 한다
        queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
        toast.success(
          lockedCustomerId ? 'API 키를 확인하고 바꿨어요' : 'API 키를 확인했어요. 이 쇼핑몰의 사이트를 골라 주세요',
        )
      },
      onError: (e: unknown) => {
        toast.error(apiMessage(e, 'API 키를 확인하지 못했어요'))
        onStale()
      },
      // 결과와 관계없이 비밀키 칸을 비운다
      onSettled: () => setApiSecret(''),
    },
  })

  return (
    <form
      className="space-y-3"
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault()
        if (!ready || connect.isPending) return
        connect.mutate({
          data: { customer_id: cid, api_key: apiKey.trim(), api_secret: apiSecret.trim() },
        })
      }}
    >
      {!lockedCustomerId && (
        <p className="text-sm text-on-surface-variant">
          네이버 광고센터 › 검색 광고 › 도구 › SA API 사용 관리에 있는 세 값을 입력해 주세요.
        </p>
      )}
      <div className="grid grid-cols-1 gap-3 max-w-xl">
        <Input
          label="customer ID"
          inputMode="numeric"
          placeholder="예: 1234567"
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          disabled={!!lockedCustomerId}
          error={invalidCid ? '숫자만 입력해 주세요' : undefined}
          className="min-h-[44px] sm:min-h-0"
        />
        <Input
          label="엑세스라이선스"
          placeholder="0100…"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          className="min-h-[44px] sm:min-h-0"
        />
        <Input
          label="비밀키"
          type="password"
          placeholder="AQAA…"
          value={apiSecret}
          onChange={(e) => setApiSecret(e.target.value)}
          autoComplete="new-password"
          spellCheck={false}
          className="min-h-[44px] sm:min-h-0"
        />
      </div>
      <Button
        type="submit"
        loading={connect.isPending}
        disabled={!ready || connect.isPending}
        className="min-h-[44px] sm:min-h-0"
      >
        {lockedCustomerId ? '확인하고 바꾸기' : '확인하고 연동'}
      </Button>
    </form>
  )
}

/** ② 요청 대기 / ③ 수락 대기 — 고객이 지금 할 일을 한 문장으로. */
function WaitingCard({
  status,
  onRecheck,
  rechecking,
  onStale,
}: {
  status: NaverStatusResponse
  onRecheck: () => void
  rechecking: boolean
  onStale: () => void
}) {
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialog } = useConfirm()
  const accept = status.stage === 'awaiting_accept'
  const cancel = useCancelNaverLinkRequest({
    mutation: {
      onSuccess: (next) => queryClient.setQueryData(getGetNaverStatusQueryKey(), next),
      onError: (e: unknown) => {
        toast.error(apiMessage(e, '입력을 취소하지 못했어요'))
        onStale()
      },
    },
  })

  return (
    <div className="space-y-4">
      {/* 단계 배지는 제목 옆(SetupPage)에 이미 있다 — 여기선 번호만 */}
      <p className="text-sm text-on-surface-variant">
        입력한 광고 계정 번호 <span className="font-medium text-on-surface">{status.submitted_customer_id}</span>
      </p>
      {accept ? (
        <p className="text-sm text-on-surface">
          저희가 연동 요청을 보냈어요. 네이버 광고센터 › 오른쪽 위 네이버 ID › 광고 계정 관리 › 광고 계정 선택 › 설정 ›
          권한 관리 › 관리 계정 연동의 &lsquo;연동 요청 중인 관리 계정&rsquo; 탭에서 수락해 주세요. 수락하신 뒤
          &lsquo;수락 확인&rsquo; 버튼을 누르면 바로 다음 단계로 넘어가요.
        </p>
      ) : (
        <p className="text-sm text-on-surface">
          번호를 받았어요. 저희가 네이버에서 연동 요청을 보내면 이 화면이 &lsquo;수락 대기&rsquo;로 바뀌어요. 지금은
          하실 일이 없어요.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={accept ? 'primary' : 'outline'}
          onClick={onRecheck}
          loading={rechecking}
          className="min-h-[44px] sm:min-h-0"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" />
          {accept ? '수락 확인' : '새로고침'}
        </Button>
        <Button
          variant="secondary"
          loading={cancel.isPending}
          className="min-h-[44px] sm:min-h-0"
          onClick={async () => {
            const ok = await confirm('입력한 번호를 취소하고 다시 입력할까요?', {
              title: '광고 계정 번호를 바꿀까요?',
              confirmText: '번호 바꾸기',
            })
            if (ok) cancel.mutate()
          }}
        >
          번호 바꾸기
        </Button>
      </div>
      <ConfirmDialog />
    </div>
  )
}

/**
 * ④ 이 쇼핑몰의 사이트 — 같은 주소의 비즈채널은 한 줄로 묶고, 체크하면 묶인 것을 전부 저장한다
 * (`naverSites.ts` — 네이버는 같은 주소에 SITE·MALL 을 따로 둔다).
 *
 * ⚠️ 체크 초기값의 근거는 사이트 응답의 `selected` 가 아니라 **상태 응답의 `business_channel_ids`** 다.
 *    저장 직후엔 상태가 `setQueryData` 로 이미 새 값인데 사이트 캐시는 재조회 전이라 낡아 있다(S5 리뷰 Major).
 */
function SiteSection({
  status,
  customerId,
  onStale,
}: {
  status: NaverStatusResponse
  customerId: string
  onStale: () => void
}) {
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialog } = useConfirm()
  const { data, isLoading, isFetching, error, refetch } = useListNaverBusinessChannels(customerId, {
    query: { retry: noRetryOnFinal },
  })
  const sites = useMemo(() => groupNaverSites(data?.items ?? []), [data])
  const savedIds = status.business_channels_set ? (status.business_channel_ids ?? []) : null

  // 목록이 처음 도착했을 때 한 번만 초기화한다 — 재조회로 사용자의 체크가 되돌아가지 않게
  const [checked, setChecked] = useState<Set<string> | null>(null)
  useEffect(() => {
    if (data && checked === null) setChecked(initialSiteKeys(sites, savedIds))
  }, [data, checked, sites, savedIds])

  const saveMutation = useSaveNaverSettings({
    mutation: {
      onSuccess: (next) => {
        queryClient.setQueryData(getGetNaverStatusQueryKey(), next)
        queryClient.invalidateQueries({ queryKey: getListNaverBusinessChannelsQueryKey(customerId) })
        // 사이드바 완료 표시(`shop.naver`)를 따라오게 한다
        queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
        toast.success('이 쇼핑몰의 사이트를 저장했어요')
      },
      // 다른 샵 사용 중(409)·사이트 불일치 거절은 목록이 낡았다는 뜻이다 — 다시 읽고 체크도 새로 잡는다
      onError: (e: unknown) => {
        toast.error(apiMessage(e, '저장하지 못했어요'))
        setChecked(null)
        onStale()
      },
    },
  })

  const toggle = (key: string) => {
    setChecked((prev) => {
      const next = new Set(prev ?? [])
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // 이 샵이 이미 저장해 둔 사이트인데 다른 샵도 쓰고 있다(겹침 — 수집기가 두 샵 모두 거부 중).
  // 저장하면 이 샵에서 빠지므로 말없이 빼지 않고 확인을 받는다(리뷰 Minor)
  const overlapping = savedIds
    ? sites.filter((s) => s.usedByOtherShop && s.ids.some((id) => savedIds.includes(id)))
    : []

  const save = async () => {
    if (overlapping.length > 0) {
      const ok = await confirm(
        `${overlapping.map((s) => s.name).join(', ')} 사이트는 다른 샵과 겹쳐서 이 쇼핑몰에서 빠져요. 이 쇼핑몰 것이 맞다면 저장하지 말고 담당자에게 문의해 주세요.`,
        { title: '겹친 사이트를 빼고 저장할까요?', confirmText: '빼고 저장' },
      )
      if (!ok) return
    }
    const ids = selectedChannelIds(sites, checked ?? new Set())
    if (ids.length === 0) {
      // ⚠️ 빈 선택은 "미확정" 이 아니라 "이 쇼핑몰 몫 없음" 확정이다 — 광고 성과가 0 으로 쌓인다
      const ok = await confirm(
        '사이트를 하나도 고르지 않으면 "이 광고 계정의 광고는 이 쇼핑몰 몫이 없다"로 확정돼요. 수집은 돌지만 이 쇼핑몰 광고 성과에는 아무것도 들어오지 않아요.',
        { title: '사이트 없이 저장할까요?', confirmText: '그대로 저장' },
      )
      if (!ok) return
    }
    saveMutation.mutate({ data: { customer_id: customerId, business_channel_ids: ids } })
  }

  if (isLoading || (data && checked === null)) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-on-surface-variant" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex gap-2 p-4 rounded-lg bg-red-50 text-red-900 text-sm">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <p>{apiMessage(error, '사이트 목록을 불러오지 못했어요')}</p>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="mt-2 min-h-[44px] sm:min-h-0 text-sm font-medium underline"
          >
            다시 시도
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3 pt-4 border-t border-outline-variant">
      <div>
        <h2 className="text-base font-semibold text-on-surface">이 쇼핑몰의 사이트(비즈채널)</h2>
        <p className="text-sm text-on-surface-variant mt-1">
          광고 계정에 등록된 사이트 중 이 쇼핑몰 것만 골라 주세요. 고른 사이트의 광고 성과만 이 쇼핑몰로 불러와요.
          쇼핑몰 주소와 같은 사이트는 미리 체크해 두었어요.
        </p>
      </div>

      {sites.length === 0 ? (
        <p className="text-sm text-on-surface-variant">이 광고 계정에 등록된 사이트가 없어요.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-on-surface-variant border-b border-outline-variant">
                <th className="py-2 pr-3 w-10" />
                <th className="py-2 pr-3">사이트</th>
                <th className="py-2 pr-3">주소</th>
                <th className="py-2">비고</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((s) => {
                const inputId = `naver-site-${s.ids[0]}`
                return (
                  <tr key={s.key} className="border-b border-outline-variant last:border-0">
                    <td className="py-2 pr-3">
                      <input
                        type="checkbox"
                        id={inputId}
                        checked={!s.usedByOtherShop && (checked?.has(s.key) ?? false)}
                        disabled={s.usedByOtherShop}
                        onChange={() => toggle(s.key)}
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <label
                        htmlFor={inputId}
                        className={s.usedByOtherShop ? 'text-on-surface-variant' : 'cursor-pointer'}
                      >
                        {s.name}
                      </label>
                    </td>
                    <td className="py-2 pr-3 text-on-surface-variant break-all">{s.address ?? '-'}</td>
                    <td className="py-2 whitespace-nowrap">
                      {s.usedByOtherShop && overlapping.includes(s) ? (
                        <Badge variant="warning">다른 샵과 겹침 · 담당자 문의</Badge>
                      ) : s.usedByOtherShop ? (
                        <Badge variant="muted">다른 샵에서 사용 중</Badge>
                      ) : s.isNaverStore ? (
                        <Badge variant="muted">스마트스토어 · 카페24 매출 아님</Badge>
                      ) : s.suggested ? (
                        <Badge variant="info">쇼핑몰 주소와 같음</Badge>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-on-surface-variant">
        하나도 고르지 않고 저장하면 &ldquo;이 쇼핑몰 몫 없음&rdquo;으로 확정돼요.
      </p>

      <Button
        onClick={save}
        loading={saveMutation.isPending}
        disabled={isFetching || saveMutation.isPending}
        className="min-h-[44px] sm:min-h-0"
      >
        저장
      </Button>
      <ConfirmDialog />
    </div>
  )
}
